import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/pro-light-svg-icons/faCoins';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../components/primitives/Panel';
import { planNameMessages } from '../../config/music24PlanNames';
import type { BrowseUsageData } from '../hooks/useBrowseUsage';
import { browseCreditMessages as messages } from '../browseMessages';
import { BrowseCreditsSummary } from './BrowseCreditsSummary';
import { BrowsePlanNote } from './BrowsePlanNote';
import { BrowseTrialOffer } from './BrowseTrialOffer';

/** The id the "No credits" rows scroll to (design b5 note). */
export const BROWSE_CREDITS_PANEL_ID = 'browse-credits';

interface BrowseCreditsPanelProps {
  usage: BrowseUsageData;
}

/** The Browse credits panel of the rail (design a1, b7). Hidden on a phone: the pill opens a sheet. */
const BrowseCreditsPanelComponent: React.FC<BrowseCreditsPanelProps> = ({ usage }) => {
  const intl = useIntl();
  return (
    <Panel as="section" id={BROWSE_CREDITS_PANEL_ID} tabIndex={-1}>
      <PanelHead
        title={intl.formatMessage(messages.title)}
        icon={<FontAwesomeIcon icon={faCoins} />}
        right={intl.formatMessage(planNameMessages[usage.plan])}
      />
      <PanelBody stack>
        <BrowseCreditsSummary usage={usage} />
        {usage.trialAvailable && <BrowseTrialOffer />}
      </PanelBody>
      <BrowsePlanNoteFoot usage={usage} />
    </Panel>
  );
};

/** The foot only when the note has something to say (Label has no next plan). */
const BrowsePlanNoteFoot: React.FC<BrowseCreditsPanelProps> = ({ usage }) => {
  if (usage.plan === 'label') {
    return null;
  }
  return (
    <PanelFoot>
      <BrowsePlanNote usage={usage} />
    </PanelFoot>
  );
};

export const BrowseCreditsPanel = TypedMemo(BrowseCreditsPanelComponent);
